import React from 'react';
import {
  SparklesIcon,
  Squares2X2Icon,
  ChartBarSquareIcon,
  PresentationChartLineIcon,
  ShieldCheckIcon,
  DocumentMagnifyingGlassIcon,
  CloudIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import {
  CapabilityGroup,
  renderCapabilityItems,
} from '../../shared/CapabilityCards';

const CAPABILITY_GROUPS = [
  {
    label: 'Copilot & embedded BI',
    accent: 'bg-brand-50/40 text-brand-700',
    borderAccent: 'border-l-brand-500',
    iconBg: 'bg-brand-50 text-brand-600',
    columns: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    items: [
      {
        icon: SparklesIcon,
        title: 'Natural Language HR Copilot',
        description:
          'Ask workforce questions in plain language, headcount, attrition, hiring, compensation, and planning, powered by Snowflake Cortex and Intelligence.',
      },
      {
        icon: Squares2X2Icon,
        title: 'Agentic Power BI Activation',
        description:
          'Each question selects the right report and applies dynamic filters from context, no hunting through workspaces, guessing bookmarks, or manual slicer resets.',
      },
      {
        icon: ChartBarSquareIcon,
        title: 'Embedded Power BI Dashboards',
        description:
          'Familiar executive and HR dashboards embedded in the copilot, coupled with Snowflake-generated charts from live query results beneath the same answer.',
      },
    ],
  },
  {
    label: 'Semantic HR depth',
    accent: 'bg-[rgba(0,18,63,0.08)] text-brand-900',
    borderAccent: 'border-l-brand-900',
    iconBg: 'bg-[rgba(0,18,63,0.08)] text-brand-900',
    items: [
      {
        icon: TableCellsIcon,
        title: 'Expandable HR Semantic Layer',
        description:
          'Start with core workforce metrics and add domains, measures, and verified queries as your program matures, headcount, attrition, recruiting, compensation, performance, benefits, planning, DEI, and beyond.',
      },
      {
        icon: PresentationChartLineIcon,
        title: 'Workforce Plan Augmentation',
        description:
          'Layer governed actuals on budget and forecast versions, run sensitivity on hiring and attrition assumptions, and support RIF or restructuring assessments.',
      },
      {
        icon: DocumentMagnifyingGlassIcon,
        title: 'HR Knowledge Search',
        description:
          'Extend beyond HRIS tables with policies, handbooks, job descriptions, offer templates, benefits documents, and compliance PDFs through Cortex Search, ask questions across structured HR data and unstructured HR knowledge.',
        featured: true,
      },
    ],
  },
  {
    label: 'Governance & platform',
    accent: 'bg-theme-migration-50/40 text-theme-migration-700',
    borderAccent: 'border-l-theme-migration-500',
    iconBg: 'bg-theme-migration-50 text-theme-migration-600',
    items: [
      {
        icon: ShieldCheckIcon,
        title: 'Enterprise Security & Governance',
        description:
          'Dynamic data masking, row access policies, and domain-specific roles, compensation, PII, and EEO separated so sensitive HR data stays protected.',
      },
      {
        icon: CloudIcon,
        title: 'Cloud Data Platform',
        description:
          'AWS and Microsoft Azure infrastructure with Microsoft Fabric and Fabric Data Factory for HR ingestion, orchestration, and hybrid analytics.',
      },
    ],
  },
];

export default function CoreCapabilities() {
  return (
    <section className="py-16 md:py-20">
      <div className="container-x">
        <div className="max-w-3xl mb-10 lg:mb-12">
          <p className="eyebrow">Platform Capabilities</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-ink-900">
            What Workforce Leaders Get
          </h2>
          <p className="mt-4 text-ink-500 leading-relaxed">
            Capabilities that compound, start simple, follow complex threads, and expand
            the platform as your workforce analytics program grows.
          </p>
        </div>

        <div className="space-y-12 lg:space-y-14">
          {CAPABILITY_GROUPS.map((group) => (
            <CapabilityGroup
              key={group.label}
              label={group.label}
              accent={group.accent}
              borderAccent={group.borderAccent}
              iconBg={group.iconBg}
              columns={group.columns}
            >
              {renderCapabilityItems(group.items, {
                iconBg: group.iconBg,
                borderAccent: group.borderAccent,
              })}
            </CapabilityGroup>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <a href="#metrics" className="btn-secondary text-sm">
            Browse HR metrics catalog →
          </a>
          <span className="text-sm text-ink-500">
            130+ reference metrics · 400+ expandable catalog scope
          </span>
        </div>
      </div>
    </section>
  );
}
